"use client";

import { usePostHog } from "posthog-js/react";
import { useEffect, useRef } from "react";

import { setSignupSourceCookie } from "@/app/actions/signup-source";
import { trackMeta } from "@/lib/meta-pixel";
import { toExpertsAudienceSource } from "@/lib/signup-source-types";

import type { AudienceSlug } from "../_config/audiences";

interface LandingTrackerProps {
  audience: AudienceSlug;
}

export function LandingTracker({ audience }: LandingTrackerProps) {
  const posthog = usePostHog();
  const fired = useRef(false);

  useEffect(() => {
    if (fired.current) {
      return;
    }
    fired.current = true;

    void setSignupSourceCookie(toExpertsAudienceSource(audience));

    // register_once: first audience the user lands on wins, so the
    // super-property survives subsequent navigations across audiences.
    // setPersonProperties($set_once) pins it as a permanent person
    // attribute for cohort/segment queries that don't read super-props.
    posthog.register_once({ landing_audience: audience });
    posthog.setPersonProperties(undefined, {
      initial_landing_audience: audience,
    });
    posthog.capture("landing_audience_viewed", { audience });

    trackMeta({
      event: "ViewContent",
      params: {
        content_name: audience,
        content_category: "experts_audience",
        content_type: "landing_page",
      },
    });
    // audience is intentionally not in deps: route param changes remount
    // the page, so a new audience always sees a fresh tracker instance.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [posthog]);

  return null;
}
